import React, { useMemo } from "react"
import keyBy from "lodash.keyby"
import { LinkOutlined } from "@ant-design/icons"
import { useOutletContext } from "react-router-dom"
import { Badge, Table, Typography } from "antd"

import { PROFILE_TYPES_LABELS } from "@components/Domain"

import TableAmountColumn from "../TableAmountColumn"
import TableInvestmentColumn from "../TableInvestmentColumn"
import { getInvestmentTierCurrency } from "./getInvestedAmount"

const LABEL_NAME = "Investment"
const LABEL_TYPE = "Profile Type"
const LABEL_CLASS = "Class"


const useColumns = ({
  targetProjectId,
  sourceProjectId,
  sourceInvestments,
  targetInvestments,
  getInvestmentAttachmentsCount,
  customAmounts,
  customTargets,
  setCustomTargets,
}) => {
  const { getProject } = useOutletContext()

  const customAmountsMap = useMemo(() =>
    keyBy(customAmounts, "investmentId")
  , [ customAmounts ])

  const sourceProject = getProject(sourceProjectId)
  const targetProject = getProject(targetProjectId)

  const sourceProjectTierIds = useMemo(() =>
    sourceProject
      ? sourceProject.tiers.map(({ name }) => name)
      : []
  , [ sourceProject ])

  const targetProjectTierIds = useMemo(() =>
    targetProject
      ? targetProject.tiers.map(({ name }) => name)
      : []
  , [ targetProject ])

  const sourceProjectName = sourceProject?.name
  const targetProjectName = targetProject?.name

  const columns = useMemo(() => {
    let projectTiersColumn = {
      title: sourceProjectName,
      children: [
        ...(
          sourceProjectTierIds
            .map(tierId => ({
              key: `${sourceProjectId}-${tierId}`,
              title: `${LABEL_CLASS} ${tierId}`,
              dataIndex: `${sourceProjectId}_${tierId}`,
              render: (_, investment) => getInvestmentTierCurrency(tierId, investment)
            }))
        )
      ]
    }

    if (targetProjectId) {
      projectTiersColumn = {
        title: targetProjectName,
        children: [
          ...(
            targetProjectTierIds
              .map(tierId => ({
                key: `${targetProjectId}-${tierId}`,
                title: `${LABEL_CLASS} ${tierId}`,
                dataIndex: `${targetProjectId}_${tierId}`,
                render: (_, { children, profileId, isIncluded, sourceInvestmentId, ...investment }) => {
                  return (
                    <TableAmountColumn
                      tierId={tierId}
                      isIncluded={isIncluded}
                      investment={investment}
                      customAmountsMap={customAmountsMap}
                      sourceInvestments={sourceInvestments}
                      sourceInvestmentId={sourceInvestmentId}
                    />
                  )
                }
              }))
          ),
        ]
      }
    }

    return [
      {
        title: "",
        children: [
          {
            key: "name",
            title: LABEL_NAME,
            dataIndex: "name",
            render: (value, { children, profileId, isIncluded, sourceInvestmentId, ...investment }) => {
              return (
                <TableInvestmentColumn
                  isIncluded={isIncluded}
                  investment={investment}
                  sourceProjectId={sourceProjectId}
                  targetInvestments={targetInvestments}
                  customTargets={customTargets}
                  setCustomTargets={setCustomTargets}
                />
              )
            },
          },
          {
            key: "profileType",
            title: LABEL_TYPE,
            dataIndex: "profileType",
            render: (value, investment) => {
              const { isIncluded } = investment
              const type = PROFILE_TYPES_LABELS[value]

              if (!isIncluded) {
                return <Typography.Text type="secondary">{type}</Typography.Text>
              }

              return type
            },
          },
        ]
      },
      projectTiersColumn,
      {
        key: "attachmentsCount",
        align: "center",
        title: <LinkOutlined />,
        dataIndex: "_attachmentsCount",
        render: (_, investment) => {
          const value = getInvestmentAttachmentsCount(investment)

          if (!value) {
            return value
          }

          const hasPlus = `${value}`.startsWith("+")

          if (hasPlus) {
            return <Badge count={value} color="#faad14" />
          }

          return <Badge count={value} color="#52c41a" />
        }
      },
      Table.EXPAND_COLUMN,
    ]
  }, [
    sourceProjectId,
    sourceProjectName,
    sourceProjectTierIds,
    sourceInvestments,
    targetProjectId,
    targetProjectName,
    targetProjectTierIds,
    targetInvestments,
    getInvestmentAttachmentsCount,
    customAmountsMap,
    customTargets,
    setCustomTargets,
  ])

  return columns
}

export default useColumns
